import { useDialogHistoryManager } from '@seek/profile-base-ui';
import { Roles } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Alert, Text, Stack } from 'braid-design-system';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Card } from 'src/components/Card/Card';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingCardItem } from 'src/components/LoadingBlock/LoadingBlock';
import { CAREER_HISTORY } from 'src/components/Router';
import { updateRoute } from 'src/components/Router/actionCreators';
import type { UpdateRouteParams } from 'src/components/Router/reducer';
import { EDIT, ADD } from 'src/consts';
import { useQuery } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import {
  retryParent,
  getFailedActionsForParent,
} from 'src/modules/redux-blend';
import { CAREER_HISTORY_FORM } from 'src/reducers/forms';
import { companyReviewsLinkClicked } from 'src/reducers/location';
import { UnconfirmedDataType } from 'src/types/graphql';

import { EditCareerHistory } from '../EditCareerHistory/EditCareerHistory';
import { trackUnconfirmedRoleImpression } from '../actionCreators';
import {
  useAddUnconfirmedRole,
  useGetConfirmedRoles,
  useGetUnconfirmedRoles,
  useRejectUnconfirmedRole,
} from '../data';

import translations from './.vocab';
import { ReadCareerHistoryCardAction } from './ReadCareerHistoryCardAction';
import { EditYearsOfExperience } from './YearsOfExperience/EditYearsOfExperience';
import { ReadYearsOfExperience } from './YearsOfExperience/ReadYearsOfExperience';
import { YearsOfExperienceSubTitle } from './YearsOfExperience/YearsOfExperienceSubTitle';
import { getYearsOfExperience } from './YearsOfExperience/yearsOfExperience.graphql';

export const ReadCareerHistory = () => {
  const { showFeature, zone, language, brand } = useAppConfig();
  const dispatch = useDispatch();

  const blend = useTypedSelector((state) => state.blend);
  const showRetry =
    getFailedActionsForParent({ blend }, CAREER_HISTORY).length > 0;
  const retryErrors = () => dispatch(retryParent(CAREER_HISTORY));
  const retryAction = showRetry ? retryErrors : undefined;

  const [addUnconfirmedRoleFn] = useAddUnconfirmedRole(ADD);
  const [rejectUnconfirmedRoleFn] = useRejectUnconfirmedRole();

  const { t } = useTranslations(translations);
  const page = useTypedSelector((state) => state.router.page);
  const mode = useTypedSelector((store) => store.router[CAREER_HISTORY].mode);
  const roleId = useTypedSelector(
    (store) => store.router[CAREER_HISTORY].params.id,
  );
  const emptyCardText = t(
    'The more you let employers know about your experience, the more you can stand out.',
  );

  const onReviewClick = ({
    companyName,
    url,
  }: {
    companyName: string;
    url: string;
  }) => {
    dispatch(companyReviewsLinkClicked(companyName));
    window.open(url);
  };

  const [drawerTitle, setDrawerTitle] = useState<string>('');
  const [displayYearsOfExperienceDialog, setDisplayYearsOfExperienceDialog] =
    useState<boolean>(false);

  useEffect(() => {
    if (mode === 'EDIT') {
      if (roleId) {
        setDrawerTitle(t('Edit role'));
      } else {
        setDrawerTitle(t('Add role'));
      }
    }
  }, [mode, t, roleId]);

  const { openDialog, closeDialog } = useDialogHistoryManager(
    setDisplayYearsOfExperienceDialog,
  );

  const editRoleFn = (id: string, confirmed: boolean) => {
    dispatch(
      updateRoute({
        to: CAREER_HISTORY,
        mode: EDIT,
        params: { id },
        analytics: {
          ...(!confirmed ? { origin: 'resume parsing nudge' } : {}),
          currentPageSectionState: 'edit',
        },
      }),
    );
  };

  const { unconfirmedRoles, loading: unconfirmedRolesLoading } =
    useGetUnconfirmedRoles();

  const {
    confirmedRoles,
    loading: confirmedRolesLoading,
    error: confirmedRolesError,
  } = useGetConfirmedRoles();

  const shouldShowTotalYearsExperience =
    showFeature('total-years-experience') && page === 'profile-page';
  const { loading: yearsOfExperienceLoading, error: yearsOfExperienceError } =
    useQuery(getYearsOfExperience, {
      skip: !shouldShowTotalYearsExperience,
      variables: { languageCode: language },
    });

  const yearsOfExperiencePending =
    shouldShowTotalYearsExperience &&
    (yearsOfExperienceLoading || yearsOfExperienceError);

  const confirmedRolesPending = confirmedRolesLoading || confirmedRolesError;

  // Ensure ReadCareerHistory is loaded after Profile component has been loaded.
  // This is to prevent race condition where unconfirmedRoleTracking
  // is triggered before Profile data has been loaded into Redux.
  const isProfileLoading = useTypedSelector(({ profile }) => profile.isLoading);

  const isLoading = Boolean(
    isProfileLoading ||
      yearsOfExperiencePending ||
      confirmedRolesPending ||
      unconfirmedRolesLoading,
  );

  const isEmpty = [...confirmedRoles, ...unconfirmedRoles].length === 0;

  const showReadYearsOfExperience =
    shouldShowTotalYearsExperience && confirmedRoles.length === 0;

  return (
    <>
      <Card
        name={CAREER_HISTORY}
        retryAction={retryAction}
        title={t('Career history')}
        emptyCardText={emptyCardText}
        isEmpty={shouldShowTotalYearsExperience ? false : isEmpty}
        cardAction={
          <ReadCareerHistoryCardAction
            isEmpty={isEmpty}
            openYearsOfExperienceDialog={openDialog}
          />
        }
        subTitle={
          shouldShowTotalYearsExperience && (
            <YearsOfExperienceSubTitle
              openYearsOfExperienceDialog={openDialog}
            />
          )
        }
        loading={isLoading}
        loader={
          <Stack space="small">
            <LoadingCardItem />
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        }
      >
        {shouldShowTotalYearsExperience && (
          <EditYearsOfExperience
            updateRouteFn={(routeParams: UpdateRouteParams) =>
              dispatch(updateRoute(routeParams))
            }
            displayDialog={displayYearsOfExperienceDialog}
            closeYearsOfExperienceDialog={closeDialog}
            setDisplayYearsOfExperienceDialog={
              setDisplayYearsOfExperienceDialog
            }
          />
        )}

        <Stack space="large">
          {showReadYearsOfExperience ? (
            <ReadYearsOfExperience emptyCardText={emptyCardText} />
          ) : null}

          <Stack space="small">
            {unconfirmedRoles.length > 0 && (
              <>
                <Alert tone="promote">
                  <Text>
                    {t('We found these roles in your resumé. Add...', {
                      brand,
                    })}
                  </Text>
                </Alert>
                <Roles
                  displayVariant="card"
                  roles={unconfirmedRoles.slice(0, 10).map((role) => ({
                    ...role,
                    editRole: editRoleFn,
                    confirmed: false,
                    addUnconfirmedRole: (_id, contentRead) => {
                      addUnconfirmedRoleFn(
                        {
                          role: { ...role },
                        },
                        { contentRead },
                      );
                    },
                    rejectUnconfirmedRole: (id) =>
                      rejectUnconfirmedRoleFn({
                        role: {
                          id,
                          dataType: UnconfirmedDataType.CareerHistory,
                          tracking: role.tracking,
                        },
                      }),
                    trackUnconfirmedRoleImpression: (roleData) =>
                      dispatch(trackUnconfirmedRoleImpression(roleData)),
                    stillInRole: !role.to,
                    zone,
                  }))}
                />
              </>
            )}

            {confirmedRoles.length > 0 && (
              <Roles
                displayVariant="card"
                roles={confirmedRoles.map((role) => ({
                  ...role,
                  editRole: editRoleFn,
                  showReviewLink: [
                    'anz-1',
                    'anz-2',
                    'asia-1',
                    'asia-3',
                    'asia-4',
                    'asia-5',
                    'asia-6',
                    'asia-7',
                  ].includes(zone),
                  onReviewClick,
                  confirmed: true,
                  stillInRole: !role.to,
                  zone,
                }))}
              />
            )}
          </Stack>
        </Stack>
      </Card>
      <FormDrawer
        title={drawerTitle}
        component={EditCareerHistory}
        card={CAREER_HISTORY}
        formName={CAREER_HISTORY_FORM}
      />
    </>
  );
};
